"use client";

import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@heroui/react";

import {
  OtherInputModalSchema,
  OtherInputModalFormData,
} from "@/lib/form-schemas";

interface OtherInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (value: string) => void;
  title: string;
  placeholder: string;
  initialValue?: string;
  showPrivacyNotice?: boolean; // Optional prop to show GDPR notice
}

export default function OtherInputModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  placeholder,
  initialValue = "",
  showPrivacyNotice = false,
}: OtherInputModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-detect if input might be requesting personal information
  const mightBePersonalInfo =
    showPrivacyNotice ||
    /email|e-mail|contact|phone|address|name/i.test(title + " " + placeholder);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<OtherInputModalFormData>({
    resolver: zodResolver(OtherInputModalSchema),
    mode: "onChange",
    defaultValues: {
      value: initialValue,
    },
  });

  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Focus sur l'input quand la modale s'ouvre
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    // Reset form when initialValue changes
    reset({ value: initialValue });
  }, [initialValue, reset]);

  const onSubmitForm = (data: OtherInputModalFormData) => {
    onConfirm(data.value.trim());
    onClose();
  };

  const handleClose = () => {
    reset({ value: initialValue });
    onClose();
  };

  return (
    <Modal
      backdrop="blur"
      classNames={{
        base: "bg-alpa-background border border-gray-600",
        header: "border-b border-gray-600",
        footer: "border-t border-gray-600",
      }}
      isOpen={isOpen}
      placement="center"
      onClose={handleClose}
    >
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <ModalHeader className="text-white">{title}</ModalHeader>
          <ModalBody>
            <Input
              classNames={{
                input: "text-white placeholder:text-gray-400",
                inputWrapper:
                  "border-gray-600 hover:border-gray-400 focus-within:border-primary bg-transparent",
              }}
              errorMessage={errors.value?.message}
              isInvalid={!!errors.value}
              placeholder={placeholder}
              variant="bordered"
              {...register("value")}
            />

            {/* Conditional GDPR Privacy Notice */}
            {mightBePersonalInfo && (
              <div className="bg-gray-500/5 border-l-4 border-gray-400 p-3 rounded mt-4">
                <p className="text-xs text-gray-400">
                  <strong>Privacy Notice:</strong> Any personal information you
                  provide will be used solely to improve your experience. We do
                  not share your information with third parties or use it for
                  marketing purposes. Your data is processed securely and you
                  can request its deletion at any time.
                </p>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              className="text-gray-400 hover:text-white"
              variant="light"
              onPress={handleClose}
            >
              Cancel
            </Button>
            <Button
              className="bg-primary hover:bg-primary-hover"
              color="primary"
              isDisabled={!isValid}
              type="submit"
            >
              Confirm
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
